<template>
  <div class="space-y-5" :class="{ 'opacity-0': loading }">
    <p class="text-lg">
      {{ totalCount }} {{ t(totalCount === 1 ? 'result' : 'results') }}
      <span v-if="basicFilter.fullText">
        {{ t('for') }} <strong>{{ basicFilter.fullText }}</strong></span
      >
    </p>
    <ul class="tags">
      <li v-if="basicFilter.openContent">
        <span>
          <span class="border inline-flex items-center text-xs">
            <span class="py-1 pl-2">{{ t('open_content') }}</span>
            <button type="button" class="p-2" @click="clearOpenContent">
              <span class="sr-only"
                >{{ t('clear') }} {{ t('open_content') }}
                {{ t('filter') }}</span
              >
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                focusable="false"
                aria-hidden="true"
              >
                <path d="M1,1 L9,9 M1,9 L9,1" stroke="currentColor"></path>
              </svg>
            </button>
          </span>
        </span>
        &nbsp;
      </li>
      <li v-if="basicFilter.hasAudioVideo">
        <span>
          <span class="border inline-flex items-center text-xs">
            <span class="py-1 pl-2">{{ t('has_audio_video') }}</span>
            <button type="button" class="p-2" @click="clearHasAudioVideo">
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                focusable="false"
                aria-hidden="true"
              >
                <path d="M1,1 L9,9 M1,9 L9,1" stroke="currentColor"></path>
              </svg>
            </button>
          </span>
        </span>
        &nbsp;
      </li>
      <li v-if="dateRange">
        <span>
          <span class="border inline-flex items-center text-xs">
            <span class="py-1 pl-2">{{ dateRange }}</span>
            <button type="button" class="p-2" @click="clearDate">
              <span class="sr-only"
                >{{ t('clear') }} {{ t('date') }} {{ t('filter') }}</span
              >
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                focusable="false"
                aria-hidden="true"
              >
                <path d="M1,1 L9,9 M1,9 L9,1" stroke="currentColor"></path>
              </svg>
            </button>
          </span>
        </span>
        &nbsp;
      </li>
      <li v-for="color in colorFilter" :key="color">
        <span>
          <span class="border inline-flex items-center text-xs">
            <span class="color" :style="`background-color: ${color}`"></span>
            <button type="button" class="p-2" @click="clearColor(color)">
              <span class="sr-only"
                >{{ t('clear') }} {{ t('color') }} {{ color }}
                {{ t('filter') }}</span
              >
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                focusable="false"
                aria-hidden="true"
              >
                <path d="M1,1 L9,9 M1,9 L9,1" stroke="currentColor"></path>
              </svg>
            </button>
          </span>
        </span>
        &nbsp;
      </li>
      <li v-for="[key, value] in computedFilter" :key="key">
        <span>
          <span class="border inline-flex items-center text-xs">
            <span class="py-1 pl-2">{{ value.text }}</span>
            <button type="button" class="p-2" @click="clearFilter(key)">
              <span class="sr-only"
                >{{ t('clear') }} {{ value.text }} {{ t('filter') }}</span
              >
              <svg
                width="10"
                height="10"
                viewBox="0 0 10 10"
                focusable="false"
                aria-hidden="true"
              >
                <path d="M1,1 L9,9 M1,9 L9,1" stroke="currentColor"></path>
              </svg>
            </button>
          </span>
        </span>
        &nbsp;
      </li>
    </ul>
  </div>
</template>

<i18n>
{
  "en": {
    "filter": "Filter",
    "result": "result",
    "results": "results",
    "for": "for",
    "from": "from",
    "to": "to",
    "clear": "clear",
    "open_content": "Open Content",
    "color": "Color",
    "date": "Date",
    "has_audio_video": "Has Audio/Video"
  },
  "de": {
    "filter": "Filter",
    "result": "Resultat",
    "results": "Resultate",
    "for": "für",
    "from": "ab",
    "to": "bis",
    "clear": "entferne",
    "open_content": "Open Content",
    "color": "Farbe",
    "date": "Datum",
    "has_audio_video": "Enthält Audio/Video"
  }
}
</i18n>

<script lang="ts">
import { computed, defineComponent, getCurrentInstance } from 'vue';
import {
  basicFilter,
  computedFilter,
  datingFilter,
  vocabularyFilter,
  colorFilter,
} from '../utils/filter';
import { VocabularyFilter } from '../@types/filter';
import { loading } from '../utils/search';
import { toggle } from '../utils/utils';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  props: {
    totalCount: {
      type: Number,
      default: 0,
    },
  },

  setup() {
    const { t } = useI18n();
    const instance = getCurrentInstance();
    const dateRange = computed((): string => {
      if (datingFilter.value) {
        if (
          datingFilter.value.from !== undefined &&
          datingFilter.value.to !== undefined
        ) {
          return `${datingFilter.value.from}–${datingFilter.value.to}`;
        }
        if (datingFilter.value.from !== undefined) {
          return `${t('from')} ${datingFilter.value.from}`;
        }
        if (datingFilter.value.to !== undefined) {
          return `${t('to')} ${datingFilter.value.to}`;
        }
      }
      return '';
    });

    const clearFilter = (key: string): void => {
      vocabularyFilter[key as keyof VocabularyFilter] = undefined;
    };
    const clearColor = (color: string): void => {
      colorFilter.value = toggle(colorFilter.value, color);
    };

    const clearDate = (): void => {
      datingFilter.value = undefined;
    };

    const clearOpenContent = (): void => {
      basicFilter.openContent = false;
    };
    const clearHasAudioVideo = (): void => {
      basicFilter.hasAudioVideo = false;
    };

    return {
      datingFilter,
      computedFilter,
      colorFilter,
      dateRange,
      clearFilter,
      clearColor,
      clearDate,
      clearOpenContent,
      clearHasAudioVideo,
      basicFilter,
      loading,
      t,
    };
  },
});
</script>
